import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import api from '../config/api';

const ImageUpload = ({ 
  currentImage, 
  onImageChange,
  disabled = false,
  className = "" 
}) => {
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const fileInputRef = useRef(null);

  // Update preview when currentImage prop changes
  useEffect(() => {
    setPreview(currentImage || null);
  }, [currentImage]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(t('Please select an image file'));
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('Image size should be less than 5MB'));
      return;
    }

    setUploading(true);

    try {
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);

      // Upload to server using api instance (includes tenant ID automatically)
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post('/api/items/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;
      
      // Clean up preview URL
      URL.revokeObjectURL(previewUrl);
      
      // Set the actual uploaded image URL
      setPreview(data.imageUrl);
      
      // Notify parent component
      if (onImageChange) {
        onImageChange(data.imageUrl);
      }

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload image. Please try again.';
      alert(errorMessage);
      setPreview(currentImage || null);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = async () => {
    // Always try to delete the current preview image from server
    if (preview) {
      try {
        // Delete from server using api instance (includes tenant ID automatically)
        await api.delete('/api/items/delete-image', {
          data: { imageUrl: preview }
        });
        console.log('Image deleted from server:', preview);
      } catch (error) {
        console.error('Error deleting image:', error);
        // Continue with removal even if server delete fails
      }
    }

    setPreview(null);
    
    if (onImageChange) {
      onImageChange(null);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || uploading}
      />

      {preview ? (
        <div className="relative group">
          <img
            src={preview}
            alt={t('Preview')}
            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
          />
          
          {!disabled && (
            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center">
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={triggerFileInput}
                  disabled={uploading}
                  className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full transition-colors"
                >
                  <Upload size={20} />
                </button>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={uploading}
                  className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          )}

          {uploading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 rounded-lg flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={triggerFileInput}
          className={`w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <ImageIcon size={48} className="text-gray-400 mb-2" />
          <p className="text-gray-500 text-center">
            {uploading ? t('Uploading...') : t('Click to upload image')}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {t('PNG, JPG up to 5MB')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;